import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, MapPin, Loader, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle, ShoppingBag, ChefHat, Bike } from 'lucide-react';
import OTPVerification from './OTPVerification';
import SignUpSuccessModal from '../SignUpSuccessModal/SignUpSuccessModal';
import { trackAuth, trackError } from '../Utils/analytics';
import { getCurrentLocationDetails, getCountriesList } from '../Utils/location';
import { SIGNUP_URL } from '../../config/api';
import '../Auth/Auth.css';

const SignUp: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [countryName, setCountryName] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [locationStatus, setLocationStatus] = useState<'detecting' | 'success' | 'denied' | 'error' | 'initial'>('initial');
  const [locationError, setLocationError] = useState('');
  const [isManualCountryEntry, setIsManualCountryEntry] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(['customer']);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();

  const roleOptions = [
    {
      id: 'customer',
      label: 'Customer',
      description: 'Order food from home cooks',
      Icon: ShoppingBag,
    },
    {
      id: 'kitchen_owner',
      label: 'Kitchen Owner',
      description: 'Cook and sell your homemade meals',
      Icon: ChefHat,
    },
    {
      id: 'delivery_partner',
      label: 'Delivery Partner',
      description: 'Deliver orders and earn money',
      Icon: Bike,
    },
  ];

  const toggleRole = (roleId: string) => {
    setSelectedRoles(prev => {
      if (prev.includes(roleId)) {
        if (prev.length === 1) return prev;
        return prev.filter(r => r !== roleId);
      }
      return [...prev, roleId];
    });
    setErrors(prev => {
      const next = { ...prev };
      delete next.roles;
      return next;
    });
  };

  // Automatically detect location when component mounts
  React.useEffect(() => {
    detectUserLocation();
  }, []);

  const detectUserLocation = async () => {
    setLocationStatus('detecting');
    setLocationError('');

    try {
      const result = await getCurrentLocationDetails();

      if ('type' in result) {
        if (result.type === 'permission_denied') {
          setLocationStatus('denied');
          setLocationError('Location access was denied. Please select your country manually below.');
        } else {
          setLocationStatus('error');
          setLocationError(result.message);
        }
        setIsManualCountryEntry(true);
      } else {
        const countries = getCountriesList();
        const isValidCode = countries.some(c => c.code === result.country_code);

        if (isValidCode) {
          setCountryName(result.country_name);
          setCountryCode(result.country_code);
          setLocationStatus('success');
        } else {
          setLocationStatus('error');
          setLocationError('Could not verify your detected country. Please select it manually.');
          setIsManualCountryEntry(true);
        }
      }
    } catch (error) {
      setLocationStatus('error');
      setLocationError('Failed to detect location. Please select your country manually.');
      setIsManualCountryEntry(true);
    }
  };

  const handleRetryLocation = () => {
    setIsManualCountryEntry(false);
    detectUserLocation();
  };

  const handleCountrySelect = (code: string, name: string) => {
    setCountryCode(code);
    setCountryName(name);
    setCountrySearch(name);
    setShowCountryDropdown(false);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.country;
      return newErrors;
    });
  };

  const filteredCountries = getCountriesList().filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation
  const validatePassword = (password: string): string[] => {
    const errors = [];
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    return errors;
  };

  // Live validation for confirm password
  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    
    if (value && password && value !== password) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: 'Passwords do not match'
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.confirmPassword;
        return newErrors;
      });
    }
  };

  // Validate all fields
  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Email validation
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Country validation
    if (!countryName.trim() || !countryCode.trim()) {
      newErrors.country = 'Country is required. Please select your country.';
    } else {
      const countries = getCountriesList();
      const isValidCode = countries.some(c => c.code === countryCode);
      if (!isValidCode) {
        newErrors.country = 'Please select a valid country from the list.';
        setIsManualCountryEntry(true);
      }
    }

    // Password validation
    if (!password) {
      newErrors.password = 'Password is required';
    } else {
      const passwordErrors = validatePassword(password);
      if (passwordErrors.length > 0) {
        newErrors.password = passwordErrors[0]; // Show first error
      }
    }

    // Confirm password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password && confirmPassword !== password) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Role validation
    if (selectedRoles.length === 0) {
      newErrors.roles = 'Please select at least one role.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if location detection is still in progress
    if (locationStatus === 'detecting') {
      setErrors({ general: 'Please wait for location detection to complete.' });
      return;
    }
    
    if (!validateForm()) {
      trackError('signup_validation_failed', 'signup');
      return;
    }

    setIsLoading(true);
    setErrors({}); // Clear any existing errors
    
    try {
      const response = await fetch(SIGNUP_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          country: countryName,
          role: selectedRoles,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Success - show OTP verification
        setShowOTPVerification(true);
        trackAuth('signup');
      } else {
        // Log the full error response for debugging
        console.error('Backend signup error response:', data);
        console.error('Response status:', response.status);
        
        // Handle different error scenarios with specific messages
        if (response.status === 409) {
          setErrors({
            general: 'An account with this email already exists. Please log in instead or use a different email address.'
          });
          trackError('signup_email_exists', 'signup');
        } else if (response.status === 400) {
          // Handle validation errors from backend
          // Check for existing account indicators first (most common case)
          const messageText = (data.message || '').toLowerCase();
          const detailText = (data.detail || '').toLowerCase();
          const errorText = (data.error || '').toLowerCase();
          const allErrorText = `${messageText} ${detailText} ${errorText}`;
          
          if (allErrorText.includes('already exists') || 
              allErrorText.includes('already registered') || 
              allErrorText.includes('email taken') ||
              allErrorText.includes('user exists') ||
              allErrorText.includes('duplicate') ||
              allErrorText.includes('conflict')) {
            setErrors({
              general: 'This email is already registered. Please log in instead or use a different email address.'
            });
            trackError('signup_email_already_exists', 'signup');
          } else if (data.message && data.message.toLowerCase().includes('email')) {
            if (data.message.toLowerCase().includes('already exists') || data.message.toLowerCase().includes('taken')) {
              setErrors({
                general: 'This email is already registered. Please log in or use a different email address.'
              });
              trackError('signup_email_taken', 'signup');
            } else if (data.message.toLowerCase().includes('invalid') || data.message.toLowerCase().includes('format')) {
              setErrors({
                email: 'Please enter a valid email address.'
              });
              trackError('signup_invalid_email', 'signup');
            } else {
              setErrors({
                email: data.message || 'Email validation failed.'
              });
              trackError('signup_email_validation', 'signup');
            }
          } else if (data.message && data.message.toLowerCase().includes('password')) {
            if (data.message.toLowerCase().includes('weak') || data.message.toLowerCase().includes('strength')) {
              setErrors({
                password: 'Password is too weak. Please use a stronger password with at least 8 characters, including uppercase, lowercase, and numbers.'
              });
              trackError('signup_weak_password', 'signup');
            } else if (data.message.toLowerCase().includes('length') || data.message.toLowerCase().includes('short')) {
              setErrors({
                password: 'Password must be at least 8 characters long.'
              });
              trackError('signup_short_password', 'signup');
            } else {
              setErrors({
                password: data.message || 'Password validation failed.'
              });
              trackError('signup_password_validation', 'signup');
            }
          } else if (data.message && data.message.toLowerCase().includes('country')) {
            setErrors({
              country: 'Please enter a valid country name.'
            });
            trackError('signup_invalid_country', 'signup');
          } else if (data.message && data.message.toLowerCase().includes('required')) {
            setErrors({
              general: 'Please fill in all required fields correctly.'
            });
            trackError('signup_missing_fields', 'signup');
          } else {
            // Fallback: if we can't identify the specific issue, show the backend message or a generic one
            setErrors({
              general: data.message || data.detail || data.error || 'Please check your information and try again.'
            });
            trackError('signup_validation_error', 'signup');
          }
        } else if (response.status === 422) {
          setErrors({
            general: 'The information provided is invalid. Please check your details and try again.'
          });
          trackError('signup_invalid_data', 'signup');
        } else if (response.status === 429) {
          setErrors({
            general: 'Too many signup attempts. Please wait a few minutes before trying again.'
          });
          trackError('signup_rate_limited', 'signup');
        } else if (response.status >= 500) {
          setErrors({
            general: 'Server error. Please try again later or contact support if the problem persists.'
          });
          trackError('signup_server_error', 'signup');
        } else {
          // Fallback for any other error codes
          setErrors({
            general: data.message || 'Registration failed. Please try again.'
          });
          trackError('signup_unknown_error', 'signup');
        }
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrors({
        general: 'Network error. Please check your internet connection and try again.'
      });
      trackError('signup_network_error', 'signup');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPVerified = () => {
    setShowOTPVerification(false);
    setShowSuccessModal(true);
  };

  const handleBackToSignup = () => {
    setShowOTPVerification(false);
  };

  const handleSuccessModalContinue = () => {
    setShowSuccessModal(false);
    navigate('/login');
  };

  if (showSuccessModal) {
    return (
      <SignUpSuccessModal 
        email={email}
        onContinue={handleSuccessModalContinue}
      />
    );
  }

  if (showOTPVerification) {
    return (
      <OTPVerification 
        email={email}
        onVerified={handleOTPVerified}
        onBack={handleBackToSignup}
      />
    );
  }

  // Show location detection loading screen
  if (locationStatus === 'detecting') {
    return (
      <div className="auth-container location-detecting-container">
        <div className="location-detecting-content">
          <div className="location-icon">
            <Loader size={60} className="location-spinner" />
          </div>
          <h2 className="location-title">Detecting Your Location</h2>
          <p className="location-description">
            We're detecting your location to automatically fill your country information.
            Please allow location access when prompted by your browser.
          </p>
          <div className="location-steps">
            <div className="location-step">
              <span className="step-number">1</span>
              <span>Click "Allow" when browser asks for location permission</span>
            </div>
            <div className="location-step">
              <span className="step-number">2</span>
              <span>We'll automatically detect your country</span>
            </div>
            <div className="location-step">
              <span className="step-number">3</span>
              <span>Complete your registration</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <h2 className="auth-title">Create Account</h2>
      <form className="auth-form" onSubmit={handleSignUp}>
        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={errors.email ? 'error' : ''}
            placeholder="Enter your email address"
            required
          />
          {errors.email && <div className="error-message">{errors.email}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="country">Country {isManualCountryEntry ? '' : '(Auto-detected)'} *</label>
          {!isManualCountryEntry && locationStatus === 'success' ? (
            <div className="country-display-container">
              <div className="country-display">
                <div className="country-info">
                  <CheckCircle size={20} className="country-success-icon" />
                  <div className="country-details">
                    <span className="country-name">{countryName}</span>
                    <span className="country-code">({countryCode})</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRetryLocation}
                  className="retry-location-small-btn"
                  title="Refresh location"
                >
                  <MapPin size={16} />
                </button>
              </div>
            </div>
          ) : (
            <div className="country-select-container">
              {locationError && (
                <div className="location-fallback-notice">
                  <AlertTriangle size={16} />
                  <span>{locationError}</span>
                </div>
              )}
              <div className="country-search-wrapper">
                <input
                  id="country"
                  type="text"
                  value={countrySearch}
                  onChange={(e) => {
                    setCountrySearch(e.target.value);
                    setShowCountryDropdown(true);
                    if (!e.target.value) {
                      setCountryCode('');
                      setCountryName('');
                    }
                  }}
                  onFocus={() => setShowCountryDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
                  className={errors.country ? 'error' : ''}
                  placeholder="Search and select your country"
                  autoComplete="off"
                />
                {showCountryDropdown && filteredCountries.length > 0 && (
                  <div className="country-dropdown">
                    {filteredCountries.map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        className={`country-dropdown-item ${countryCode === country.code ? 'selected' : ''}`}
                        onMouseDown={() => handleCountrySelect(country.code, country.name)}
                      >
                        <span className="country-dropdown-name">{country.name}</span>
                        <span className="country-dropdown-code">{country.code}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleRetryLocation}
                className="retry-location-small-btn"
                title="Retry auto-detection"
              >
                <MapPin size={16} />
                <span>Retry auto-detect</span>
              </button>
            </div>
          )}
          {errors.country && <div className="error-message">{errors.country}</div>}
        </div>

        <div className="form-group">
          <label>I want to join as *</label>
          <p className="role-selector-hint">Select one or more roles — you can always use all features you pick.</p>
          <div className="role-selector-grid">
            {roleOptions.map(({ id, label, description, Icon }) => {
              const isSelected = selectedRoles.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  className={`role-card${isSelected ? ' role-card--selected' : ''}`}
                  onClick={() => toggleRole(id)}
                  aria-pressed={isSelected}
                >
                  {isSelected && (
                    <span className="role-card__check">
                      <CheckCircle size={16} />
                    </span>
                  )}
                  <span className="role-card__icon">
                    <Icon size={28} />
                  </span>
                  <span className="role-card__label">{label}</span>
                  <span className="role-card__desc">{description}</span>
                </button>
              );
            })}
          </div>
          {errors.roles && <div className="error-message">{errors.roles}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <div className="password-input-container">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={errors.password ? 'error' : ''}
              placeholder="Create a strong password"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password && <div className="error-message">{errors.password}</div>}
          <div className="password-requirements">
            <small>Password must contain at least 8 characters, including uppercase, lowercase, and a number</small>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password *</label>
          <div className="password-input-container">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => handleConfirmPasswordChange(e.target.value)}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Confirm your password"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
        </div>

        {errors.general && <div className="error-message general-error">{errors.general}</div>}

        <button 
          type="submit" 
          className="auth-button"
          disabled={isLoading || Object.keys(errors).length > 0}
        >
          {isLoading ? 'Creating Account...' : 'Sign Up'}
        </button>
      </form>

      <div className="auth-switch">
        Already have an account?
        <Link to="/login">Log In</Link>
      </div>
    </div>
  );
};

export default SignUp;